import type { InboxFilters } from '@/types/inbox'

/**
 * Filtros da lista de conversas na URL (`/conversas?...`), para sobreviverem a
 * refresh, voltar e link compartilhado. Valores padrão ficam fora da URL.
 */

export const TAB_KEYS = [
  'todas',
  'minhas',
  'nao-lidas',
  'pendentes',
  'humanas',
  'ia',
] as const
export type TabKey = (typeof TAB_KEYS)[number]

export const STATUS_VALUES = ['OPEN', 'FINALIZED', 'ARCHIVED', 'all'] as const
export type StatusFilter = (typeof STATUS_VALUES)[number]

const INTERACTION_VALUES = [
  'interacted',
  'not_interacted',
  'needs_human_action',
] as const
const WINDOW_VALUES = ['open', 'expiring', 'closed', 'not_applicable'] as const

export interface ConversasSearch {
  tab?: Exclude<TabKey, 'todas'>
  q?: string
  status?: Exclude<StatusFilter, 'OPEN'>
  assignedTo?: string
  channelId?: string
  departmentId?: string
  tagId?: string
  campaignId?: string
  interaction?: Exclude<InboxFilters['interaction'], 'all'>
  windowState?: Exclude<InboxFilters['windowState'], 'all'>
}

export interface ConversasFilterState {
  tab: TabKey
  q: string
  status: StatusFilter
  assignedTo: string
  channelId: string
  departmentId: string
  tagId: string
  campaignId: string
  interaction: InboxFilters['interaction']
  windowState: InboxFilters['windowState']
}

export const DEFAULT_CONVERSAS_FILTERS: ConversasFilterState = {
  tab: 'todas',
  q: '',
  status: 'OPEN',
  assignedTo: 'all',
  channelId: 'all',
  departmentId: 'all',
  tagId: 'all',
  campaignId: 'all',
  interaction: 'all',
  windowState: 'all',
}

function oneOf<T extends string>(
  values: readonly T[],
  raw: unknown
): T | undefined {
  return typeof raw === 'string' && (values as readonly string[]).includes(raw)
    ? (raw as T)
    : undefined
}

function id(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw.length > 0 && raw !== 'all'
    ? raw
    : undefined
}

export function validateConversasSearch(
  raw: Record<string, unknown>
): ConversasSearch {
  const tab = oneOf(TAB_KEYS, raw.tab)
  const status = oneOf(STATUS_VALUES, raw.status)
  return {
    tab: tab === 'todas' ? undefined : tab,
    q: typeof raw.q === 'string' && raw.q.trim() ? raw.q : undefined,
    status: status === 'OPEN' ? undefined : status,
    assignedTo: id(raw.assignedTo),
    channelId: id(raw.channelId),
    departmentId: id(raw.departmentId),
    tagId: id(raw.tagId),
    campaignId: id(raw.campaignId),
    interaction: oneOf(INTERACTION_VALUES, raw.interaction),
    windowState: oneOf(WINDOW_VALUES, raw.windowState),
  }
}

export function filterStateFromSearch(
  search: ConversasSearch
): ConversasFilterState {
  return {
    tab: search.tab ?? 'todas',
    q: search.q ?? '',
    status: search.status ?? 'OPEN',
    assignedTo: search.assignedTo ?? 'all',
    channelId: search.channelId ?? 'all',
    departmentId: search.departmentId ?? 'all',
    tagId: search.tagId ?? 'all',
    campaignId: search.campaignId ?? 'all',
    interaction: search.interaction ?? 'all',
    windowState: search.windowState ?? 'all',
  }
}

/** Aplica um patch ao estado e devolve a busca canônica (sem padrões). */
export function searchFromFilterState(
  state: ConversasFilterState
): ConversasSearch {
  return validateConversasSearch({ ...state })
}
